import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsAPI } from '../../services/api';
import { AuthContext } from '../../context/AuthContext';

function UserStatusCards() {
  const navigate = useNavigate();
  const { id: userId } = useContext(AuthContext);
  const [statusGroups, setStatusGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUserLeads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await leadsAPI.getAssignedLeads(userId, { limit: 100 });
      const leads = response.data.leads || response.data;
      
      // Group leads by status
      const grouped = leads.reduce((acc, lead) => {
        const status = lead.progressStatus;
        if (!acc[status]) {
          acc[status] = {
            _id: status,
            leads: [],
            count: 0,
            lastUpdate: lead.updatedAt
          };
        }
        acc[status].leads.push({
          _id: lead._id,
          schoolName: lead.schoolName,
          updatedAt: lead.updatedAt,
          assignedTo: lead.assignedTo
        });
        acc[status].count++;
        if (new Date(lead.updatedAt) > new Date(acc[status].lastUpdate)) {
          acc[status].lastUpdate = lead.updatedAt;
        }
        return acc;
      }, {});

      setStatusGroups(Object.values(grouped).sort((a, b) => a._id.localeCompare(b._id)));
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch your leads');
    } finally {
      setLoading(false);
    }
  }, [userId]);

    useEffect(() => {
      fetchUserLeads();
  }, [fetchUserLeads]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Closed Won': return 'bg-green-50 border-green-200 text-green-800';
      case 'Closed Lost': return 'bg-red-50 border-red-200 text-red-800';
      case 'Cold Lead': return 'bg-gray-50 border-gray-200 text-gray-800';
      case 'Prospecting': return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'Qualified': return 'bg-emerald-50 border-emerald-200 text-emerald-800';
      case 'Demo Scheduled / Done': return 'bg-purple-50 border-purple-200 text-purple-800';
      case 'Evaluation / Pilot': return 'bg-indigo-50 border-indigo-200 text-indigo-800';
      case 'Decision': return 'bg-orange-50 border-orange-200 text-orange-800';
      default: return 'bg-slate-50 border-slate-200 text-slate-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Lead Status</h1>
          <p className="text-gray-600">View your assigned leads organized by their current progress status</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {statusGroups.map((group) => (
            <div
              key={group._id}
              className={`rounded-lg border-2 p-6 hover:shadow-lg transition-shadow ${getStatusColor(group._id)}`}
            >
              {/* Status Header */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-1">{group._id}</h3>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-xs">
                    Last update: {formatDate(group.lastUpdate)}
                  </span>
                </div>
              </div>

              {/* Schools List */}
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {group.leads.map((lead) => (
                  <div
                    key={lead._id}
                    className="bg-white/70 rounded-md p-3 border border-white/50 hover:bg-white/90 cursor-pointer transition-colors"
                    onClick={() => navigate('/user/lead-listing')}
                    title="Click to view in My Leads"
                  >
                    <div className="font-medium text-sm mb-1 truncate" title={lead.schoolName}>
                      {lead.schoolName}
                    </div>
                    <div className="text-xs text-gray-600">
                      <div>Updated: {formatDate(lead.updatedAt)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {group.leads.length === 0 && (
                <div className="text-center text-sm text-gray-500 py-8">
                  No leads in this status
                </div>
              )}
            </div>
          ))}
        </div>

        {statusGroups.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">No leads assigned to you</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserStatusCards;